import React, { useEffect, useState, useRef } from "react";
import "./verifyModal.css";
import HeroSection from "../images/HeroSection.svg";
import heroSectionMobile from "../images/heroSectionMobile.svg";
import { toast } from "sonner";
import axios from "axios";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const blockedDomains = ["gmail.com", "yahoo.com", "hotmail.com", "outlook.com"];

const VerifyModal = ({ isOpen, onClose }) => {
  const modalRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [email, setEmail] = useState("");
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isUploaded, setIsUploaded] = useState(false); // Added to toggle success screen

  useEffect(() => {
    function scrollNotifications() {
      var element = document.getElementById("scrollDiv");
      element.scrollTop = -element.scrollHeight;
    }

    scrollNotifications();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    validateAndSetFile(selectedFile);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    validateAndSetFile(droppedFile);
  };

  const validateAndSetFile = (selectedFile) => {
    if (!selectedFile) {
      setError("We’ll need a PDF file to move ahead. Upload and try again!");
      return;
    }
    if (selectedFile.type !== "application/pdf") {
      setError(
        "That doesn’t look like a valid PDF. Try uploading another file!"
      );
      setFile(null);
      return;
    }
    if (selectedFile.size > MAX_FILE_SIZE) {
      setError("File too large! Please upload a file under 10 MB.");
      setFile(null);
      return;
    }

    setError("");
    setFile(selectedFile);

    // Progress animation
    setProgress(0);
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 100);
  };

  const removeFile = (e) => {
    e.preventDefault();
    setFile(null);
    setProgress(0);
  };

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) return setError("Please enter your email.");
    if (!file) return setError("Please upload a PDF file.");

    if (!EMAIL_REGEX.test(email)) {
      toast.error("Oops! That email doesn’t seem right. Try again?");
      return;
    }

    const domain = email?.split("@")[1]?.toLowerCase();
    if (blockedDomains?.includes(domain)) {
      toast.error("Personal emails aren’t allowed. Try using your work email.");
      return;
    }

    const formData = new FormData();
    formData.append("email", email);
    formData.append("file", file);
    formData.append("name", "");

    setLoading(true);
    setError("");

    try {
      const baseUrl = process.env.REACT_APP_TAMPERPROOF_URL;
      const endpoint = "/dashboard/tamper-proof/";
      const url = `${baseUrl}${endpoint}`;

      const res = await axios.post(url, formData);

      if (res.status === 200) {
        setIsUploaded(true);
        // toast.success(res.data?.message || "File uploaded successfully!");
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.detail ||
          "We’re facing a tiny glitch. Hang tight and retry."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleScrollToFooter = () => {
    const footer = document.getElementById("tamper-form");
    if (footer) {
      footer.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <div
        className={`modal-overlay ${isOpen ? "show" : ""}`}
        onClick={onClose}
      />
      <div
        id="scrollDiv"
        ref={modalRef}
        className={`center-modal ${isOpen ? "show" : ""}`}
      >
        <div
          className="verify-left-container"
          style={{
            backgroundImage: `url(${
              isMobile ? heroSectionMobile : HeroSection
            })`,
          }}
        ></div>

        <div className="verify-right-container">
          <div className="right-cont-header">
            <h1>Upload PDF to Verify Tampering</h1>
            <img
              src="/images/Button.svg"
              alt="X"
              className="verify-cross-btn"
              onClick={onClose}
            />
          </div>

          {/* Conditional Rendering */}
          {!isUploaded ? (
            // ===================== FORM =====================
            <form className="verify-form" onSubmit={handleSubmit}>
              <div className="email-label">
                <label>
                  Email <span>*</span>
                </label>
                <input
                  type="email"
                  placeholder="Enter email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div
                className="upload-box"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                onChange={handleFileChange}
                onClick={() => document.getElementById("fileInput").click()}
              >
                <input
                  id="fileInput"
                  type="file"
                  accept="application/pdf"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
                <img
                  src="/images/document-upload.svg"
                  alt="upload"
                  onClick={() => document.getElementById("fileInput").click()}
                  style={{ cursor: "pointer" }}
                />
                <h3>Click to Upload or Drag & Drop PDF</h3>
                <p>Support for PDF Files</p>
                <p>Click to Upload or Drag & Drop PDF</p>
                {error && <p className="error">{error}</p>}
              </div>

              {file && (
                <div className="file-preview">
                  <div className="pdf-image">
                    <img src="/images/Frame.svg" alt="" />
                  </div>
                  <div className="file-info">
                    <p className="file-name">{file.name}</p>
                    <p className="file-size">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>

                  <div className="progress-wrapper">
                    <div
                      className="progress-bar"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="progress-percent">{progress}%</p>

                  <button className="remove-btn" onClick={removeFile}>
                    <img src="/images/close-circle.svg" alt="" />
                  </button>
                </div>
              )}

              <div className="verify-footer">
                <button type="submit" disabled={loading}>
                  {loading ? "Uploading..." : "Submit Now"}
                </button>
                <p>Get Instant Results | 💰 1 Free Credit</p>
              </div>
            </form>
          ) : (
            // SUCCESS
            <>
              <div className="success-wrapper">
                <div className="success-container">
                  <img src="/images/verify.svg" alt="" />
                  <h3>Your PDF Submitted Successfully</h3>
                  <p>
                    We are analysing the PDF and will send the report to your
                    mailbox at the earliest
                  </p>
                </div>
                <button
                  className="create-acc-btn"
                  onClick={(e) => {
                    handleScrollToFooter();
                    if (isOpen) onClose(e);
                  }}
                >
                  Create an Account to Explore More
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default VerifyModal;
